import { useMemo } from 'react';

import type { OAuthHandlers } from './OAuthHandlers';
import { useOAuthHandlerFactory } from './useOAuthHandlerFactory';

interface UseAppOAuthProps {
	friendlyName: string;
	appSource: string;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onFailAction?: () => void;
	analyticsAttributes: {
		source: string;
		isNewlyEnabled?: boolean;
		surface?: string;
	};
}

/**
 * Convenience wrapper around useOAuthHandlerFactory for the common case where
 * a single connector is known at hook-call time.
 */
export const useAppOAuth = ({
	friendlyName,
	appSource,
	outboundAuthUrl,
	onCompletedAction,
	onFailAction,
	analyticsAttributes: { source, isNewlyEnabled, surface },
}: UseAppOAuthProps): OAuthHandlers => {
	const createHandler = useOAuthHandlerFactory();

	return useMemo(
		() =>
			createHandler({
				friendlyName,
				appSource,
				outboundAuthUrl,
				onCompletedAction,
				onFailAction,
				analyticsAttributes: { source, isNewlyEnabled, surface },
			}),
		[
			createHandler,
			friendlyName,
			appSource,
			outboundAuthUrl,
			onCompletedAction,
			onFailAction,
			source,
			isNewlyEnabled,
			surface,
		],
	);
};
